import { SitePickerForOrg } from '@monorepo/shared/componentsV2/SitePickerForOrg';
import { Permissions } from 'mapistry-shared';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrgProjectsForUserWithPermission } from '../../../../../shared/hooks/useOrgProjectsForUserWithPermission';
import { useFormTemplateContext } from '../../EditFormTemplatePage/FormTemplateContext';

/** Find a project this form is used on that I am an admin on. If the form isn't in use yet, just a project I am an admin on. */
function useDefaultProjectForPreview(
  organizationId: string,
): string | undefined {
  const { projects: projectsWithAdminAccess } =
    useOrgProjectsForUserWithPermission(
      organizationId,
      Permissions.PROJECT_DETAILS_UPDATE,
    );
  const { formTemplateUsage } = useFormTemplateContext();

  // we restrict access to the form builder to people who are admins on all sites a form template is used on,
  // so we know the user is an admin on any site the template is used on (July 2026)
  const projectUsingThisForm = formTemplateUsage?.[0]?.projectId;
  const randomProjectWithAdminAccess = projectsWithAdminAccess?.[0]?.id;

  return projectUsingThisForm || randomProjectWithAdminAccess;
}

/** e.g. if we reached the form builder via a calendar widget, that should have put projectId onto location state */
function useGetProjectIdFromReferrerPage(): string | undefined {
  const { state } = useLocation();
  return state?.projectId;
}

function useProjectToDefaultTo(organizationId: string): string | undefined {
  const projectIdFromReferrerPage = useGetProjectIdFromReferrerPage();
  const projectIdToDefaultTo = useDefaultProjectForPreview(organizationId);
  return projectIdFromReferrerPage || projectIdToDefaultTo;
}

type ProjectPickerForFormPreviewProps = {
  organizationId: string;
  projectId?: string;
  setProjectId: (projectId: string) => void;
};

export function ProjectPickerForFormPreview({
  organizationId,
  projectId,
  setProjectId,
}: ProjectPickerForFormPreviewProps) {
  const projectIdToDefaultTo = useProjectToDefaultTo(organizationId);

  // once the project permissions finished loading, update the project picker
  useEffect(() => {
    if (projectIdToDefaultTo && !projectId) {
      setProjectId(projectIdToDefaultTo);
    }
  }, [projectIdToDefaultTo, projectId, setProjectId]);

  return (
    <SitePickerForOrg
      organizationId={organizationId}
      dontSaveSpaceForErrorText
      isLabelHidden
      label="Site to preview"
      name="form-preview-site"
      value={projectId || projectIdToDefaultTo}
      onChange={(e) => setProjectId(e.target.value as string)}
      permission={Permissions.PROJECT_DETAILS_UPDATE}
    />
  );
}
